import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { StageBadge, fmtAmount, fmtDate, fileToBase64, bankFieldsFor, CREDIT_CARD_FIELDS, fmtBillingPeriod, fmtRemaining } from "@/lib/util";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, XCircle, CurrencyBtc, ShieldCheck, Paperclip, ChatCircle, Link as LinkIcon, ArrowClockwise, Copy, Sparkle, Bank, PencilSimple, Warning, ArrowSquareOut } from "@phosphor-icons/react";

const EDITABLE_STAGES = ["draft", "pending_audit", "rejected", "approved"];

// Public block-explorer URL for a tx hash on a given network. Returns null if
// we don't know an explorer for that network — the UI just hides the button.
function explorerUrl(network, hash) {
  if (!hash) return null;
  const n = String(network || "").toUpperCase();
  const h = encodeURIComponent(hash);
  if (n === "TRC20" || n === "TRON" || n === "TRX") return `https://tronscan.org/#/transaction/${h}`;
  if (n === "ERC20" || n === "ETH" || n === "ETHEREUM") return `https://etherscan.io/tx/${h}`;
  if (n === "BEP20" || n === "BSC" || n === "BNB") return `https://bscscan.com/tx/${h}`;
  if (n === "POLYGON" || n === "MATIC") return `https://polygonscan.com/tx/${h}`;
  if (n === "SOL" || n === "SOLANA") return `https://solscan.io/tx/${h}`;
  if (n === "BTC" || n === "BITCOIN") return `https://mempool.space/tx/${h}`;
  return null;
}

export default function RequestDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ payment_reference: "", tx_hash: "", note: "" });
  const [payFiles, setPayFiles] = useState([]);
  // Edit dialog state
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ amount: "", network: "", wallet_address: "", bank_details: {} });
  const [currencyList, setCurrencyList] = useState([]);
  const [payerVendors, setPayerVendors] = useState([]);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get(`/payments/${id}`);
    setP(data);
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    api.get("/payer-vendors").then((r) => setPayerVendors(r.data || [])).catch(() => setPayerVendors([]));
  }, []);

  if (!p) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  const act = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); await load(); toast.success(successMsg); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const approve = () => act(() => api.post(`/payments/${id}/approve`, { note: null }), "Approved");
  const approverApprove = () => act(() => api.post(`/payments/${id}/approver-approve`, { note: null }), "Approved — sent to audit");
  const reject = () => act(async () => {
    if (!rejectNote) throw new Error("Reason required");
    const endpoint = p.stage === "pending_approval" ? "approver-reject" : "reject";
    await api.post(`/payments/${id}/${endpoint}`, { note: rejectNote });
    setRejectOpen(false);
    setRejectNote("");
  }, "Rejected");

  const markPaid = () => act(async () => {
    const attachments = [];
    for (const f of payFiles) attachments.push(await fileToBase64(f));
    await api.post(`/payments/${id}/mark-paid`, { ...payForm, attachments });
    setPayOpen(false);
    setPayForm({ payment_reference: "", tx_hash: "", note: "" });
    setPayFiles([]);
  }, "Marked as paid");

  const verify = () => act(() => api.post(`/payments/${id}/verify`, { note: null }), "Verified");
  // Pass the currently-visible tx_hash/network as an override so legacy
  // records with a missing `network` field on the doc can still be verified.
  const verifyChain = () => act(
    () => api.post(`/payments/${id}/verify-blockchain`, {
      tx_hash: p?.tx_hash || null,
      network: p?.network || null,
    }),
    "Blockchain checked",
  );
  const verifySlip = (index) => act(() => api.post(`/payments/${id}/verify-slip`, { attachment_index: index }), "Slip analyzed by AI");
  const forceReject = () => {
    const reason = window.prompt(
      `⚠ FORCE-REJECT this request from stage "${p.stage}"?\n\nThis works even after paid / verified.\nEnter a reason (required):`
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A reason is required"); return; }
    act(() => api.post(`/payments/${id}/force-reject`, { reason: trimmed }), "Force-rejected");
  };
  const restore = () => {
    if (!window.confirm(`Restore ${p.reference} back into the queue?\n\nIt'll be moved back to the stage it was at before the rejection.`)) return;
    act(() => api.post(`/payments/${id}/restore`), "Restored — back in queue");
  };
  const deletePayment = () => {
    if (!window.confirm(
      `HARD DELETE ${p.reference}?\n\n` +
      `Vendor: ${p.vendor_name}\n${p.currency_code} ${p.net_amount}\nStage: ${p.stage}\n\n` +
      `This CANNOT be undone. The audit log will retain the deletion event.`
    )) return;
    act(async () => {
      await api.delete(`/payments/${id}`);
      toast.success("Deleted");
      navigate("/queue");
    }, null);
  };
  const revertStage = (targetStage) => {
    if (!targetStage || !p) return;
    const reason = window.prompt(
      `⏪ Revert ${p.reference} from "${p.stage}" back to "${targetStage}"?\n\n` +
      `Fields owned by any stages walked past (paid_by, tx_hash, blockchain, verified_by, etc.) will be CLEARED so the request is truly re-actionable.\n\n` +
      `Enter a reason:`,
      "Reverted for correction",
    );
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) { toast.error("A reason is required"); return; }
    act(() => api.post(`/payments/${id}/revert-stage`, {
      target_stage: targetStage, reason: trimmed,
    }), `Reverted to ${targetStage}`);
  };
  const copy = (val) => {
    if (!val) return;
    // Double-payment safety: hard-warn when copying anything from a paid/verified request
    if (["paid", "verified"].includes(p.stage)) {
      const ok = window.confirm(
        `⚠ WARNING · POSSIBLE DOUBLE PAYMENT\n\n` +
        `This request is already ${p.stage.toUpperCase()}.\n\n` +
        `${p.reference} · ${p.brand_name}\n` +
        `${p.currency_code} ${p.net_amount} → ${p.vendor_name}\n\n` +
        `Copying this value may cause you to pay a second time.\n\nContinue?`
      );
      if (!ok) return;
    }
    navigator.clipboard.writeText(String(val));
    toast.success("Copied");
  };
  const copyAll = () => {
    if (p.payment_mode === "crypto") {
      const t = `${p.vendor_name}\nNetwork: ${p.network}\nWallet: ${p.wallet_address}\nAmount: ${p.net_amount} ${p.currency_code}`;
      copy(t);
    } else {
      const b = p.bank_details || {};
      const lines = [`${p.vendor_name}`];
      Object.entries(b).forEach(([k, v]) => { if (v) lines.push(`${k.replace(/_/g, " ")}: ${v}`); });
      lines.push(`Amount: ${p.net_amount} ${p.currency_code}`);
      copy(lines.join("\n"));
    }
  };
  const addComment = () => act(async () => {
    if (!comment.trim()) throw new Error("Comment cannot be empty");
    await api.post(`/payments/${id}/comments`, { body: comment });
    setComment("");
  }, "Comment added");

  const openEdit = async () => {
    setEditForm({
      amount: String(p.amount ?? ""),
      currency_code: p.currency_code || "",
      network: p.network || "",
      wallet_address: p.wallet_address || "",
      wallet_expires_at: p.wallet_expires_at ? p.wallet_expires_at.slice(0, 16) : "",  // trim to "YYYY-MM-DDTHH:mm" for datetime-local
      bank_details: { ...(p.bank_details || {}) },
    });
    // Preload currencies for the network dropdown (crypto only needs it)
    if (currencyList.length === 0) {
      try {
        const { data } = await api.get("/currencies");
        setCurrencyList(data);
      } catch (_) { /* ignore */ }
    }
    setEditOpen(true);
  };
  const saveEdit = () => act(async () => {
    const patch = {};
    const newAmt = parseFloat(editForm.amount);
    if (Number.isFinite(newAmt) && newAmt > 0 && newAmt !== p.amount) patch.amount = newAmt;
    if (p.payment_mode === "crypto") {
      if (editForm.currency_code && editForm.currency_code !== p.currency_code) patch.currency_code = editForm.currency_code;
      if (editForm.network && editForm.network !== p.network) patch.network = editForm.network;
      if (editForm.wallet_address && editForm.wallet_address !== p.wallet_address) patch.wallet_address = editForm.wallet_address.trim();
    } else if (p.payment_mode === "bank") {
      // Only send bank_details if any field actually changed
      const orig = p.bank_details || {};
      const now = editForm.bank_details || {};
      const keys = new Set([...Object.keys(orig), ...Object.keys(now)]);
      const changed = Array.from(keys).some((k) => (orig[k] || "") !== (now[k] || ""));
      if (changed) patch.bank_details = now;
    }
    // Wallet-expiry timer — always sent when the user touched the field (empty
    // string tells backend to clear it)
    const origExp = p.wallet_expires_at ? p.wallet_expires_at.slice(0, 16) : "";
    if ((editForm.wallet_expires_at || "") !== origExp) {
      patch.wallet_expires_at = editForm.wallet_expires_at
        ? new Date(editForm.wallet_expires_at).toISOString()
        : "";
    }
    if (Object.keys(patch).length === 0) throw new Error("No changes to save");
    // Client-side hint: if amount changed >5% at approved stage, warn user
    // it will bounce back for re-audit (server enforces the rule). Skipped
    // for post-payment super-admin corrections — those stay at their stage.
    if (p.stage === "approved" && patch.amount !== undefined && p.amount > 0) {
      const deltaPct = Math.abs(patch.amount - p.amount) / p.amount * 100;
      if (deltaPct > 5) {
        if (!window.confirm(`Amount change is ${deltaPct.toFixed(1)}% (over the 5% cap). This request will be sent back to audit for re-approval. Continue?`)) {
          throw new Error("Cancelled");
        }
      }
    }
    await api.patch(`/payments/${id}`, patch);
    setEditOpen(false);
  }, "Saved");

  const isAuditor = user?.role === "auditor" || user?.role === "admin" || user?.role === "requester";
  const isPayer = user?.role === "payer" || user?.role === "admin";
  const isOwnerRequester = p?.requester_id === user?.id;
  const canVerify = isPayer || isOwnerRequester;
  const isAssignedApprover = p?.assigned_approver_id === user?.id;
  const canApproverAct = (isAssignedApprover || user?.role === "admin") && p?.stage === "pending_approval";
  const canEdit = (user?.is_super_admin || EDITABLE_STAGES.includes(p.stage))
    && (user?.role === "admin" || p.requester_id === user?.id);
  const isPostPaymentEdit = ["paid", "verified"].includes(p.stage) && !!user?.is_super_admin;
  const currentCurrency = currencyList.find((c) => c.code === p.currency_code);
  const bankFields = p.payment_mode === "bank" ? bankFieldsFor(p.currency_code) : (p.payment_mode === "credit_card" ? CREDIT_CARD_FIELDS : []);
  // Wallet-expiry check: only meaningful before the payer sends money. Shows a
  // hard red banner + blocks the payer's "Mark as paid" button from acting on
  // stale wallet addresses.
  const preservePayStages = ["draft", "pending_approval", "pending_audit", "approved", "rejected"];
  const walletExpiresAtMs = p.wallet_expires_at ? Date.parse(p.wallet_expires_at) : null;
  const walletExpired = walletExpiresAtMs && Number.isFinite(walletExpiresAtMs)
    && walletExpiresAtMs < Date.now()
    && preservePayStages.includes(p.stage);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl">
      <div className="mb-2 text-sm"><Link to="/queue" className="text-blue-700 hover:underline">← Queue</Link></div>
      {walletExpired && (
        <div data-testid="wallet-expired-banner" className="mb-4 rounded-2xl border-2 border-red-400 bg-red-50 px-4 py-3 flex items-start gap-3 shadow-sm">
          <Warning size={22} weight="fill" className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-heading font-bold text-red-900 text-base">DO NOT PAY · Wallet address needs re-verification</div>
            <div className="text-xs text-red-800 mt-0.5">
              Wallet validity window has passed — <span className="font-mono font-bold">{fmtRemaining(p.wallet_expires_at)}</span>.
              Confirm the wallet with the requester before sending funds.
            </div>
          </div>
        </div>
      )}
      {/* Pre-expiry countdown — shown when wallet_expires_at is set and still in the future */}
      {!walletExpired && walletExpiresAtMs && walletExpiresAtMs > Date.now() && preservePayStages.includes(p.stage) && (
        <div data-testid="wallet-countdown-banner" className="mb-4 rounded-2xl border border-amber-300 bg-amber-50/70 px-4 py-2.5 flex items-center gap-2 text-xs">
          <span className="font-heading font-bold text-amber-900 uppercase tracking-wider text-[11px]">⏳ Wallet valid for</span>
          <span className="font-mono font-black text-amber-900 text-sm" data-testid="wallet-remaining">{fmtRemaining(p.wallet_expires_at)}</span>
          <span className="text-amber-800">— confirm with requester if close to expiry</span>
        </div>
      )}
      <header className="flex flex-wrap items-start justify-between gap-4 mb-6 sm:mb-8">
        <div className="min-w-0">
          <div className="overline">Request</div>
          <div className="flex items-center flex-wrap gap-3 mt-1">
            <h1 className="font-heading text-2xl sm:text-3xl font-black tracking-tight font-mono break-all">{p.reference}</h1>
            {p.brand_allocations && p.brand_allocations.length > 1 ? (
              <div data-testid="detail-brand-multi" className="flex flex-wrap gap-1.5">
                <span className="inline-flex items-center px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-violet-100 border border-violet-300 text-violet-900">
                  {p.brand_allocations.length}-brand split
                </span>
                {p.brand_allocations.map((a) => (
                  <span key={a.brand_id} className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900" title={`${fmtAmount(a.amount, p.currency_code)} to ${a.brand_name}`}>
                    {a.brand_name} · {fmtAmount(a.amount, p.currency_code)}
                  </span>
                ))}
              </div>
            ) : (
              <span data-testid="detail-brand-pill" className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 text-amber-900">
                {p.brand_name || "—"}
              </span>
            )}
            <StageBadge stage={p.stage} approvalStep={p.approval_step} />
          </div>
          <div className="text-sm text-muted-foreground mt-1">{p.vendor_name} · {fmtBillingPeriod(p.billing_month, p.billing_month_end)}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <Button data-testid="detail-edit" onClick={openEdit} disabled={busy} variant="outline" className="rounded-xl h-10 px-4">
              <PencilSimple size={16} className="mr-2" weight="bold" /> Edit
            </Button>
          )}
          {canApproverAct && (
            <>
              <Button data-testid="detail-approver-approve" onClick={approverApprove} disabled={busy} className="rounded-xl h-10 px-4" style={{ background: "linear-gradient(135deg, #a78bfa, #7c3aed)", color: "white" }}>
                <CheckCircle size={16} className="mr-2" weight="bold" /> Approve → send to audit
              </Button>
              <Button data-testid="detail-approver-reject" onClick={() => setRejectOpen(true)} disabled={busy} className="btn-danger rounded-xl h-10 px-4">
                <XCircle size={16} className="mr-2" weight="bold" /> Reject
              </Button>
            </>
          )}
          {isAuditor && p.stage === "pending_audit" && (
            <>
              <Button data-testid="detail-approve" onClick={approve} disabled={busy} className="amber-pill rounded-xl h-10 px-4">
                <CheckCircle size={16} className="mr-2" weight="bold" /> Approve
              </Button>
              <Button data-testid="detail-reject" onClick={() => setRejectOpen(true)} disabled={busy} className="btn-danger rounded-xl h-10 px-4">
                <XCircle size={16} className="mr-2" weight="bold" /> Reject
              </Button>
            </>
          )}
          {isPayer && p.stage === "approved" && (
            <Button data-testid="detail-mark-paid" onClick={() => setPayOpen(true)} disabled={busy || walletExpired} className="amber-pill rounded-xl h-10 px-4" title={walletExpired ? "Wallet address expired — confirm with requester first" : undefined}>
              <CurrencyBtc size={16} className="mr-2" weight="bold" /> Mark as paid
            </Button>
          )}
          {canVerify && p.stage === "paid" && (
            <Button data-testid="detail-verify" onClick={verify} disabled={busy} className="amber-pill rounded-xl h-10 px-4">
              <ShieldCheck size={16} className="mr-2" weight="bold" /> Verify
            </Button>
          )}
          {p.stage === "rejected" && (user?.role === "admin" || p.requester_id === user?.id) && (
            <Button data-testid="detail-restore" onClick={restore} disabled={busy}
                    className="rounded-xl h-10 px-4 bg-emerald-600 hover:bg-emerald-700 text-white">
              <ArrowClockwise size={16} className="mr-2" weight="bold" /> Restore
            </Button>
          )}
          {user?.role === "admin" && p.stage !== "rejected" && (
            <Button data-testid="detail-force-reject" onClick={forceReject} disabled={busy}
                    variant="outline" className="rounded-xl h-10 px-4 border-red-300 text-red-700 hover:bg-red-50">
              <XCircle size={16} className="mr-2" weight="bold" /> Force reject
            </Button>
          )}
          {user?.role === "admin" && !["rejected", "draft"].includes(p.stage) && (() => {
            // Only offer stages EARLIER than the current one — moving forward
            // is what the normal action buttons are for.
            const order = ["pending_approval", "pending_audit", "approved", "paid", "verified"];
            const idx = order.indexOf(p.stage);
            const options = idx > 0 ? order.slice(0, idx) : [];
            if (options.length === 0) return null;
            const labels = {
              pending_approval: "Pending approval",
              pending_audit:    "Pending audit",
              approved:         "Approved",
              paid:             "Paid",
            };
            return (
              <Select value="" onValueChange={(v) => v && revertStage(v)}>
                <SelectTrigger data-testid="detail-revert-stage" className="h-10 w-44 rounded-xl border-amber-300 bg-amber-50 text-amber-800 font-semibold text-sm">
                  <SelectValue placeholder="⏪ Revert to…" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`detail-revert-${s}`}>
                      ⏪ {labels[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          })()}
          {user?.role === "admin" && (
            <Button data-testid="detail-delete" onClick={deletePayment} disabled={busy}
                    variant="outline" className="rounded-xl h-10 px-4 border-red-500 text-red-700 hover:bg-red-100">
              <XCircle size={16} className="mr-2" weight="bold" /> Delete
            </Button>
          )}
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Recipient / Bank details with copy */}
          {(p.payment_mode !== "cash") && (
            <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Bank size={18} weight="duotone" className="text-amber-700" />
                  <div className="font-heading font-bold text-lg">Recipient details</div>
                </div>
                <Button data-testid="detail-copy-all" onClick={copyAll} size="sm" variant="outline" className="rounded-xl h-9">
                  <Copy size={14} className="mr-1.5" /> Copy all
                </Button>
              </div>
              {p.payment_mode === "crypto" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <CopyableKV label="Network" value={p.network} onCopy={copy} />
                  <CopyableKV label="Wallet address" value={p.wallet_address} onCopy={copy} mono truncate />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {Object.entries(p.bank_details || {}).map(([k, v]) => (
                    v ? <CopyableKV key={k} label={k.replace(/_/g, " ")} value={v} onCopy={copy} mono={k !== "account_name" && k !== "bank_name" && k !== "branch"} /> : null
                  ))}
                  {Object.keys(p.bank_details || {}).length === 0 && (
                    <div className="text-sm text-slate-500 md:col-span-2">No bank details captured for this request.</div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Amounts card */}
          <div className="border border-border bg-white p-6">
            <div className="overline mb-3">Financials</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              <KV label="Currency" value={p.currency_code} mono />
              <KV label="Amount" value={fmtAmount(p.amount)} mono />
              <KV label="Charges" value={fmtAmount(p.charges)} mono />
              <KV label="Net" value={fmtAmount(p.net_amount)} mono strong />
              <KV label="Mode" value={(p.payment_mode || "").toUpperCase()} />
              <KV label="Network" value={p.network || "—"} mono />
              <div className="col-span-2 sm:col-span-3 md:col-span-1">
                <KV label="Wallet" value={p.wallet_address || "—"} mono wrap />
              </div>
              <KV label="Requester" value={p.requester_name} />
            </div>
            {p.remarks && (<div className="mt-4 border-t border-border pt-4"><div className="overline mb-1">Remarks</div><p className="text-sm">{p.remarks}</p></div>)}
          </div>

          {/* Payer vendor picker — USD bank payments only (other currencies
              settle locally with no processor fee) */}
          {p.payment_mode === "bank" && (p.currency_code || "").toUpperCase() === "USD" && (
            <PayerVendorPicker
              payment={p}
              vendors={payerVendors}
              canEdit={isPayer && ["approved", "pending_audit"].includes(p.stage)}
              onSelect={async (payload) => {
                // payload = { payer_vendor_id, settlement_currency, settlement_rate }
                await api.post(`/payments/${id}/set-payer-vendor`, payload);
                await load();
                toast.success(payload.payer_vendor_id ? "Vendor & FX saved" : "Payer vendor cleared");
              }}
            />
          )}

          {/* Blockchain */}
          {p.payment_mode === "crypto" && p.tx_hash && (() => {
            const bcVerified = p.blockchain?.verified === true;
            const bcTallied = p.blockchain?.tallied === true;
            const bcAddrMatch = p.blockchain?.addr_match === true;
            const bcAddrExplicitMismatch = p.blockchain?.addr_match === false;
            // Green box requires ALL three gates — chain confirmed, amount
            // tallies, AND recipient wallet matches. Without addr_match we
            // used to green-light txs paid to the wrong wallet.
            const fullyOk = bcVerified && bcTallied && bcAddrMatch;
            const manuallyVerified = p.stage === "verified" && !fullyOk;
            return (
            <div className={`p-6 rounded-2xl transition-all ${fullyOk
              ? "bg-gradient-to-br from-emerald-50 via-emerald-100/70 to-emerald-50 border-2 border-emerald-400 shadow-lg shadow-emerald-100"
              : bcAddrExplicitMismatch
                ? "bg-red-50/70 border-2 border-red-400"
                : manuallyVerified
                  ? "bg-amber-50/60 border-2 border-amber-300"
                  : "border border-border bg-white"}`}>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className={`overline ${fullyOk ? "text-emerald-800" : bcAddrExplicitMismatch ? "text-red-800" : ""}`}>Blockchain verification</div>
                  {fullyOk && (
                    <span data-testid="detail-onchain-verified-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-emerald-600 text-white animate-pulse">
                      ✓ Verified on-chain
                    </span>
                  )}
                  {!fullyOk && bcAddrExplicitMismatch && (
                    <span data-testid="detail-onchain-addr-mismatch-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-red-600 text-white">
                      ⚠ Wallet mismatch — paid to wrong address
                    </span>
                  )}
                  {!fullyOk && !bcAddrExplicitMismatch && manuallyVerified && (
                    <span data-testid="detail-manually-verified-pill" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black uppercase tracking-wider bg-amber-500 text-white">
                      ✓ Manually verified · not on-chain
                    </span>
                  )}
                </div>
                <Button data-testid="detail-verify-chain" onClick={verifyChain} disabled={busy} size="sm" variant="outline" className="rounded-none">
                  <ArrowClockwise size={14} className="mr-1.5" /> Check on chain
                </Button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <KV label="Network" value={p.network} mono />
                <div className="sm:col-span-2 md:col-span-2">
                  <div className="overline">Tx hash</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    <div data-testid="detail-tx-hash" className="font-mono text-xs break-all min-w-0 flex-1 basis-full sm:basis-auto bg-white/60 border border-amber-100 rounded-lg px-2 py-1.5">
                      {p.tx_hash}
                    </div>
                    <button
                      data-testid="detail-tx-hash-copy"
                      onClick={() => copy(p.tx_hash)}
                      title="Copy tx hash"
                      className="p-2 rounded-lg text-slate-500 hover:text-amber-700 hover:bg-amber-50 shrink-0"
                    >
                      <Copy size={14} weight="bold" />
                    </button>
                    {explorerUrl(p.network, p.tx_hash) && (
                      <a
                        data-testid="detail-tx-hash-explorer"
                        href={explorerUrl(p.network, p.tx_hash)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open on ${p.network} explorer`}
                        className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs font-semibold text-amber-800 hover:text-amber-950 hover:bg-amber-50 shrink-0"
                      >
                        <ArrowSquareOut size={14} weight="bold" /> Explorer
                      </a>
                    )}
                  </div>
                </div>
                <KV label="Status" value={p.blockchain?.verified ? "Confirmed" : (p.blockchain?.error ? "Error" : "Not checked")}
                    strong={p.blockchain?.verified === true}
                    tone={p.blockchain?.verified === true ? "emerald" : (p.blockchain?.error ? "red" : undefined)}
                />
                <KV label="From" value={p.blockchain?.from_address || "—"} mono wrap />
                <KV label="To" value={p.blockchain?.to_address || "—"} mono wrap />
                <KV label="On-chain amount" value={p.blockchain ? fmtAmount(p.blockchain?.onchain_amount, p.blockchain?.token || "") : "—"} mono />
                <KV label="Confirmations" value={p.blockchain?.confirmations ?? "—"} mono />
                <KV label="Tallied vs request" value={p.blockchain?.tallied === true ? "YES" : p.blockchain?.tallied === false ? "NO" : "—"}
                    strong={p.blockchain?.tallied === true}
                    tone={p.blockchain?.tallied === true ? "emerald" : (p.blockchain?.tallied === false ? "red" : undefined)}
                />
                <KV label="Recipient matches wallet"
                    value={p.blockchain?.addr_match === true ? "YES" : p.blockchain?.addr_match === false ? "NO — different address" : "—"}
                    strong={p.blockchain?.addr_match === true}
                    tone={p.blockchain?.addr_match === true ? "emerald" : (p.blockchain?.addr_match === false ? "red" : undefined)}
                />
                <KV label="Checked at" value={fmtDate(p.blockchain?.checked_at)} />
              </div>
              {p.blockchain?.error && <p className="mt-3 text-xs text-red-700 font-mono">{p.blockchain.error}</p>}
              {(user?.role === "admin" || user?.role === "payer" || p.paid_by_id === user?.id) && (
                <div className="mt-4 pt-4 border-t border-amber-100">
                  <button
                    data-testid="detail-tx-hash-edit"
                    onClick={async () => {
                      const next = window.prompt(
                        `Enter the corrected transaction hash for ${p.reference}.\nNetwork: ${p.network || "(none)"}\nCurrent: ${p.tx_hash || "(none)"}\n\nThe on-chain check will rerun automatically against the new hash.`,
                        p.tx_hash || ""
                      );
                      if (next === null) return;
                      const trimmed = next.trim();
                      if (!trimmed || trimmed === p.tx_hash) return;
                      try {
                        const { data } = await api.patch(`/payments/${p.id}/tx-hash`, { tx_hash: trimmed });
                        setP(data);
                        const bc = data?.blockchain;
                        if (bc?.tallied === true && bc?.addr_match === true) {
                          toast.success("✓ Tx hash updated · auto-verified on-chain");
                        } else if (bc?.addr_match === false) {
                          toast.error("⚠ Tx hash updated but recipient wallet mismatches");
                        } else if (bc?.tallied === false) {
                          toast.warning("Tx hash updated but on-chain amount mismatches");
                        } else {
                          toast.success("Tx hash updated · re-verifying on chain…");
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || e.message || "Update failed");
                      }
                    }}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200"
                  >
                    Correct tx hash & recheck
                  </button>
                  <p className="text-[11px] text-slate-500 mt-1.5">
                    {user?.role === "admin"
                      ? "Works on paid & verified requests. The on-chain check reruns automatically."
                      : "Fix a wrong hash you pasted while marking paid. The on-chain check reruns automatically."}
                  </p>
                </div>
              )}
            </div>
            );
          })()}

          {/* Timeline */}
          <div className="border border-border bg-white p-6">
            <div className="overline mb-4">Timeline</div>
            <ol className="space-y-4 border-l-2 border-border pl-6">
              {(p.history || []).map((h, i) => (
                <li key={i} className="relative" data-testid={`timeline-${h.stage}-${i}`}>
                  <span className="absolute -left-[29px] top-1 w-3 h-3 border-2 border-white rounded-full" style={{ background: "linear-gradient(135deg, #4ADE80, #15803D)" }} />
                  <div className="flex items-center gap-2">
                    <StageBadge stage={h.stage} />
                    <span className="text-xs text-muted-foreground">{fmtDate(h.at)}</span>
                  </div>
                  <div className="text-sm mt-1">{h.actor_name} <span className="text-muted-foreground">({h.actor_role})</span>{h.note ? ` — ${h.note}` : ""}</div>
                </li>
              ))}
            </ol>
          </div>

          {/* Comments */}
          <div className="border border-border bg-white p-6">
            <div className="overline mb-3 flex items-center gap-2"><ChatCircle size={14} /> Comments</div>
            <div className="space-y-3 mb-4">
              {(p.comments || []).length === 0 && <p className="text-sm text-muted-foreground">No comments yet.</p>}
              {(p.comments || []).map((c) => (
                <div key={c.id} className="border border-border p-3" data-testid={`comment-${c.id}`}>
                  <div className="text-xs text-muted-foreground"><span className="font-semibold text-gray-900">{c.author_name}</span> · {c.author_role} · {fmtDate(c.created_at)}</div>
                  <div className="text-sm mt-1 whitespace-pre-wrap">{c.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Textarea data-testid="comment-input" placeholder="Add a comment…" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} className="flex-1" />
              <Button data-testid="comment-submit" onClick={addComment} disabled={busy} className="rounded-none">Post</Button>
            </div>
          </div>
        </div>

        <aside className="space-y-6">
          <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
            <div className="overline mb-3 flex items-center gap-2"><Paperclip size={14} /> Attachments</div>
            <ul className="space-y-2">
              {(p.attachments || []).length === 0 && <li className="text-sm text-slate-500">No attachments.</li>}
              {(p.attachments || []).map((a) => (
                <li key={a.index} className="flex items-center gap-2">
                  <button
                    data-testid={`att-${a.index}`}
                    className="flex-1 text-left text-xs font-mono flex items-center gap-2 hover:underline"
                    onClick={async () => {
                      const { data } = await api.get(`/payments/${id}/attachments/${a.index}`);
                      const link = document.createElement("a");
                      link.href = `data:${data.mime_type};base64,${data.data_base64}`;
                      link.download = data.filename;
                      link.click();
                    }}
                  >
                    <LinkIcon size={12} /> <span className="truncate">{a.filename}</span>
                  </button>
                  {isPayer && (p.stage === "paid" || p.stage === "verified") && (a.mime_type || "").startsWith("image/") && (
                    <button data-testid={`att-verify-slip-${a.index}`}
                      onClick={() => verifySlip(a.index)}
                      className="text-[10px] font-semibold px-2 py-1 rounded-md bg-amber-100 text-amber-800 hover:bg-amber-200"
                      title="Verify amount with AI">
                      <Sparkle size={10} weight="fill" className="inline mr-0.5" /> AI check
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {p.slip_verification && (
            <div className={`rounded-3xl p-4 sm:p-6 shimmer-border ${p.slip_verification.amount_matches === true ? "border border-emerald-300 bg-emerald-50/50" : p.slip_verification.amount_matches === false ? "border border-red-300 bg-red-50/50" : "glass"}`}>
              <div className="overline mb-2 flex items-center gap-2"><Sparkle size={12} weight="fill" /> AI slip verdict</div>
              <div className="text-sm font-semibold mb-2">{p.slip_verification.verdict || (p.slip_verification.ok ? "Analyzed" : "Failed")}</div>
              {p.slip_verification.ok && (
                <div className="text-xs space-y-1 font-mono">
                  <div>Amount: <b>{p.slip_verification.amount ?? "—"}</b> {p.slip_verification.currency || ""} {p.slip_verification.amount_matches === true ? "✓" : p.slip_verification.amount_matches === false ? "✗" : ""}</div>
                  {p.slip_verification.recipient && <div className="truncate">To: {p.slip_verification.recipient}</div>}
                  {p.slip_verification.transaction_id && <div className="truncate">Txn: {p.slip_verification.transaction_id}</div>}
                  {p.slip_verification.status && <div>Status: {p.slip_verification.status}</div>}
                </div>
              )}
              {p.slip_verification.error && <div className="text-xs text-red-700 mt-1">{p.slip_verification.error}</div>}
              <div className="text-[10px] text-slate-500 mt-2">Checked by {p.slip_verification.checked_by} · {fmtDate(p.slip_verification.checked_at)}</div>
            </div>
          )}

          <div className="glass rounded-3xl p-4 sm:p-6 shimmer-border">
            <div className="overline mb-3">Actions summary</div>
            <div className="space-y-3 text-xs">
              <PersonRow label="Requested" name={p.requester_name} at={p.created_at} tint="slate" />
              <PersonRow label="Audited" name={p.audited_by_name} at={p.audited_at} tint="sky" note={p.audit_note} />
              <PersonRow label="Paid" name={p.paid_by_name} at={p.paid_at} tint="emerald" note={p.payment_reference} />
              <PersonRow label="Verified" name={p.verified_by_name} at={p.verified_at} tint="amber" />
            </div>
          </div>
        </aside>
      </div>

      {/* Reject dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent data-testid="reject-dialog" className="rounded-none">
          <DialogHeader><DialogTitle>Reject request</DialogTitle></DialogHeader>
          <Label>Reason (visible to requester)</Label>
          <Textarea data-testid="reject-note" value={rejectNote} onChange={(e) => setRejectNote(e.target.value)} rows={4} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectOpen(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="reject-confirm" onClick={reject} className="btn-danger rounded-xl h-10 px-4">Confirm reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mark paid dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pay-dialog" className="rounded-none max-w-lg">
          <DialogHeader><DialogTitle>Mark as paid</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Payment reference (bank txn ID / receipt no.)</Label>
              <Input data-testid="pay-ref" value={payForm.payment_reference} onChange={(e) => setPayForm({ ...payForm, payment_reference: e.target.value })} />
            </div>
            {p.payment_mode === "crypto" && (
              <div>
                <Label>Transaction hash *</Label>
                <Input data-testid="pay-tx" value={payForm.tx_hash} onChange={(e) => setPayForm({ ...payForm, tx_hash: e.target.value })} className="font-mono text-xs" />
              </div>
            )}
            <div>
              <Label>Note</Label>
              <Textarea data-testid="pay-note" value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} rows={2} />
            </div>
            <div>
              <Label>Upload proof (screenshot)</Label>
              <input data-testid="pay-files" type="file" multiple onChange={(e) => setPayFiles(Array.from(e.target.files))} className="text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)} className="rounded-none">Cancel</Button>
            <Button data-testid="pay-confirm" onClick={markPaid} className="amber-pill rounded-xl h-10 px-4">Confirm payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Edit dialog — amount + bank/wallet */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-dialog" className="rounded-2xl max-w-lg">
          <DialogHeader><DialogTitle>Edit request · <span className="font-mono">{p.reference}</span></DialogTitle></DialogHeader>
          {p.stage === "approved" && (
            <div data-testid="edit-approval-warning" className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
              <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
              <span>Small edits stay <b>approved</b>. Amount changes over <b>5%</b> (or currency / payment-mode changes) will send it back to <b>Pending Audit</b> for re-approval.</span>
            </div>
          )}
          {isPostPaymentEdit && (
            <div data-testid="edit-post-payment-warning" className="rounded-xl border-2 border-red-400 bg-red-50 px-3 py-2 text-xs text-red-900 flex items-start gap-2">
              <Warning size={14} weight="fill" className="mt-0.5 flex-shrink-0 text-red-700" />
              <span>
                <b>Post-payment correction · super-admin only</b>. This request is already <b>{p.stage}</b>. Your changes will NOT re-trigger audit — the request will stay {p.stage}. The edit is logged in the timeline with your name as the corrector.
              </span>
            </div>
          )}
          <div className="space-y-3">
            <div>
              <Label>Amount ({p.currency_code})</Label>
              <Input
                data-testid="edit-amount"
                type="number"
                step="any"
                min="0"
                value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                className="mt-1 font-mono"
              />
              <div className="text-[10px] text-slate-500 mt-1">Charges + net total will be recalculated automatically.</div>
            </div>

            {p.payment_mode === "crypto" && (
              <>
                <div>
                  <Label>Currency (crypto)</Label>
                  <Select
                    value={editForm.currency_code}
                    onValueChange={(v) => {
                      // Auto-reset network to first supported by the new currency
                      const cur = currencyList.find((c) => c.code === v);
                      const nets = cur?.networks || [];
                      const nextNet = nets.includes(editForm.network) ? editForm.network : (nets[0] || "");
                      setEditForm({ ...editForm, currency_code: v, network: nextNet });
                    }}
                  >
                    <SelectTrigger data-testid="edit-currency" className="mt-1"><SelectValue placeholder="Select currency" /></SelectTrigger>
                    <SelectContent>
                      {currencyList.filter((c) => c.is_crypto).map((c) => (
                        <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code} — {c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="text-[10px] text-slate-500 mt-1">
                    Switching stablecoin (e.g. USDT → USDC) or chain (e.g. TRC20 → Solana) will re-validate the wallet address.
                  </div>
                </div>
                <div>
                  <Label>Network</Label>
                  <Select value={editForm.network} onValueChange={(v) => setEditForm({ ...editForm, network: v })}>
                    <SelectTrigger data-testid="edit-network" className="mt-1"><SelectValue placeholder="Select network" /></SelectTrigger>
                    <SelectContent>
                      {(currencyList.find((c) => c.code === editForm.currency_code)?.networks || (p.network ? [p.network] : [])).map((n) => (
                        <SelectItem key={n} value={n}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Wallet address</Label>
                  <Input
                    data-testid="edit-wallet"
                    value={editForm.wallet_address}
                    onChange={(e) => setEditForm({ ...editForm, wallet_address: e.target.value })}
                    className="mt-1 font-mono text-xs"
                    placeholder="Paste wallet address"
                  />
                </div>
                <div>
                  <Label>Wallet valid for <span className="text-slate-500 font-normal">(optional)</span></Label>
                  {(() => {
                    const toLocalInput = (d) => {
                      const pad = (n) => String(n).padStart(2, "0");
                      return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                    };
                    const applyDuration = (value, unit) => {
                      const n = parseInt(value, 10);
                      if (!Number.isFinite(n) || n <= 0) {
                        setEditForm({ ...editForm, wallet_expires_at: "", _dur_value: value, _dur_unit: unit });
                        return;
                      }
                      const mins = unit === "hours" ? n * 60 : n;
                      const d = new Date(Date.now() + mins * 60_000);
                      setEditForm({ ...editForm, wallet_expires_at: toLocalInput(d), _dur_value: value, _dur_unit: unit });
                    };
                    const durValue = editForm._dur_value ?? "";
                    const durUnit = editForm._dur_unit || "mins";
                    const remainingLabel = editForm.wallet_expires_at
                      ? fmtRemaining(new Date(editForm.wallet_expires_at).toISOString())
                      : "no expiry set";
                    return (
                      <>
                        <div className="mt-1 flex items-center gap-2">
                          <Input
                            data-testid="edit-wallet-expiry-value"
                            type="number"
                            min="1"
                            value={durValue}
                            onChange={(e) => applyDuration(e.target.value, durUnit)}
                            placeholder="e.g. 30"
                            className="w-24"
                          />
                          <Select value={durUnit} onValueChange={(v) => applyDuration(durValue, v)}>
                            <SelectTrigger data-testid="edit-wallet-expiry-unit" className="w-28">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="mins">mins</SelectItem>
                              <SelectItem value="hours">hours</SelectItem>
                            </SelectContent>
                          </Select>
                          <button
                            type="button"
                            data-testid="edit-wallet-expiry-clear"
                            onClick={() => setEditForm({ ...editForm, wallet_expires_at: "", _dur_value: "", _dur_unit: "mins" })}
                            className="px-2.5 py-1 rounded-lg text-xs font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50"
                          >
                            Clear
                          </button>
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs">
                          <span className="text-slate-500">Expires in:</span>
                          <span data-testid="edit-wallet-expiry-remaining" className="font-mono font-bold text-amber-900">
                            {remainingLabel}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-500 mt-2">
                          After this duration, the queue will warn the payer <b>NOT to pay</b> until the wallet is re-verified. Leave blank to disable.
                        </div>
                      </>
                    );
                  })()}
                </div>
              </>
            )}

            {p.payment_mode === "bank" && bankFields.length > 0 && (
              <div className="grid grid-cols-1 gap-3">
                {bankFields.map((bf) => (
                  <div key={bf.k}>
                    <Label>{bf.label}{bf.required ? " *" : ""}</Label>
                    <Input
                      data-testid={`edit-bank-${bf.k}`}
                      value={editForm.bank_details?.[bf.k] || ""}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (bf.upper) val = val.toUpperCase();
                        setEditForm({ ...editForm, bank_details: { ...editForm.bank_details, [bf.k]: val } });
                      }}
                      className={`mt-1 ${bf.mono ? "font-mono" : ""}`}
                    />
                  </div>
                ))}
              </div>
            )}

            {p.payment_mode === "credit_card" && (
              <div className="grid grid-cols-1 gap-3">
                {CREDIT_CARD_FIELDS.map((bf) => (
                  <div key={bf.k}>
                    <Label>{bf.label}{bf.required ? " *" : ""}</Label>
                    <Input
                      data-testid={`edit-cc-${bf.k}`}
                      value={editForm.bank_details?.[bf.k] || ""}
                      onChange={(e) => setEditForm({ ...editForm, bank_details: { ...editForm.bank_details, [bf.k]: e.target.value } })}
                      className={`mt-1 ${bf.mono ? "font-mono" : ""}`}
                      maxLength={bf.k === "last_4" ? 4 : undefined}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-xl">Cancel</Button>
            <Button data-testid="edit-save" onClick={saveEdit} disabled={busy} className="amber-pill rounded-xl h-10 px-4">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KV({ label, value, mono, strong, truncate, wrap, tone }) {
  const toneClass = tone === "emerald" ? "text-emerald-800" : tone === "red" ? "text-red-700" : "";
  return (
    <div>
      <div className="overline">{label}</div>
      <div className={`mt-1 text-sm ${mono ? "font-mono" : ""} ${strong ? "font-bold" : ""} ${truncate ? "truncate" : ""} ${wrap ? "break-all" : ""} ${toneClass}`}>{value}</div>
    </div>
  );
}

function PersonRow({ label, name, at, tint, note }) {
  const color = { sky: "text-sky-800 bg-sky-50 border-sky-200",
                  emerald: "text-emerald-800 bg-emerald-50 border-emerald-200",
                  amber: "text-amber-800 bg-amber-50 border-amber-200",
                  slate: "text-slate-800 bg-slate-50 border-slate-200" }[tint] || "text-slate-800 bg-slate-50 border-slate-200";
  const done = !!name;
  return (
    <div className={`rounded-xl border p-3 ${done ? color : "bg-white/40 border-slate-200 text-slate-400"}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
          <div className="mt-0.5 font-semibold text-sm truncate">{name || "Pending"}</div>
        </div>
        {at && <div className="text-[10px] font-mono opacity-70 whitespace-nowrap">{fmtDate(at)}</div>}
      </div>
      {note && <div className="mt-1 text-xs italic opacity-80 line-clamp-2">{note}</div>}
    </div>
  );
}

function CopyableKV({ label, value, mono, truncate, onCopy }) {
  return (
    <div className="rounded-xl bg-white/60 border border-amber-100 p-3 group">
      <div className="overline uppercase">{label}</div>
      <div className="mt-1 flex items-center gap-2">
        <div className={`text-sm flex-1 ${mono ? "font-mono text-xs break-all" : ""} ${truncate ? "truncate" : ""}`}>{value || "—"}</div>
        {value && (
          <button data-testid={`copy-${label}`} onClick={() => onCopy(value)} className="p-1.5 rounded-md hover:bg-amber-100 text-slate-500 hover:text-amber-700 opacity-60 group-hover:opacity-100 transition-opacity">
            <Copy size={13} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

function PayerVendorPicker({ payment, vendors, canEdit, onSelect }) {
  const [selected, setSelected] = useState(payment.payer_vendor_id || "");
  const [settlementCcy, setSettlementCcy] = useState(payment.settlement_currency || "");
  const [settlementRate, setSettlementRate] = useState(
    payment.settlement_rate != null ? String(payment.settlement_rate) : "",
  );
  useEffect(() => {
    setSelected(payment.payer_vendor_id || "");
    setSettlementCcy(payment.settlement_currency || "");
    setSettlementRate(payment.settlement_rate != null ? String(payment.settlement_rate) : "");
  }, [payment.payer_vendor_id, payment.settlement_currency, payment.settlement_rate]);

  const picked = vendors.find((v) => v.id === selected);
  const pct = picked ? Number(picked.charge_percent || 0) : 0;
  const amt = Number(payment.amount || 0);
  const fee = amt * pct / 100;
  const final = amt + fee; // in invoice currency
  const rateNum = Number(settlementRate) || 0;
  const settlementAmount = rateNum > 0 ? final * rateNum : 0;
  const hasStored = !!payment.payer_vendor_id;
  const invoiceCcy = payment.currency_code || "USD";

  const save = () => onSelect({
    payer_vendor_id: selected || null,
    settlement_currency: settlementCcy || null,
    settlement_rate: settlementRate ? Number(settlementRate) : null,
  });
  const clear = () => onSelect({ payer_vendor_id: null });

  return (
    <div data-testid="payer-vendor-picker" className="border border-purple-200 bg-purple-50/40 p-6 rounded-none">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline text-purple-900">Payer vendor · USD bank fulfilment</div>
          <div className="text-xs text-slate-600 mt-1">
            {canEdit ? "Pick the processor you'll use. Optionally add the currency + FX rate you're actually paying in (e.g. AED)."
                    : (hasStored ? "The payer selected this processor. Amounts below reflect what they actually spent."
                                 : "Awaiting payer to pick a vendor.")}
          </div>
        </div>
      </div>

      {canEdit ? (
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-end">
            <div className="flex-1">
              <Label>Payer vendor</Label>
              <select
                data-testid="payer-vendor-select"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="mt-1 w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm"
              >
                <option value="">— None —</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name} · {v.charge_percent}%</option>
                ))}
              </select>
            </div>
          </div>

          {/* Settlement currency + rate — appear once a vendor is picked */}
          {selected && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-xl bg-white/70 border border-purple-100 p-3">
              <div>
                <Label>Settlement currency <span className="text-slate-400 font-normal">(optional)</span></Label>
                <select
                  data-testid="settlement-currency"
                  value={settlementCcy || "__none__"}
                  onChange={(e) => {
                    const val = e.target.value === "__none__" ? "" : e.target.value;
                    setSettlementCcy(val);
                    if (!val) setSettlementRate(""); // clear rate when None
                  }}
                  className="mt-1 w-full rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-mono uppercase"
                >
                  <option value="__none__">— None —</option>
                  {["AED", "INR", "HKD", "SGD", "USD"]
                    .filter((c) => c !== (invoiceCcy || "").toUpperCase())
                    .map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                </select>
              </div>
              <div>
                <Label>
                  Exchange rate{" "}
                  <span className="text-slate-400 font-normal">
                    (1 {invoiceCcy} = X {settlementCcy || "?"})
                  </span>
                </Label>
                <Input
                  data-testid="settlement-rate"
                  type="number"
                  step="any"
                  min="0"
                  value={settlementRate}
                  onChange={(e) => setSettlementRate(e.target.value)}
                  placeholder={settlementCcy ? "e.g. 3.67" : "Pick a settlement currency first"}
                  disabled={!settlementCcy}
                  className={`mt-1 font-mono ${!settlementCcy ? "opacity-50 cursor-not-allowed bg-slate-100" : ""}`}
                />
              </div>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              data-testid="payer-vendor-save"
              onClick={save}
              className="rounded-xl h-10 px-4 amber-pill"
              disabled={!selected}
            >
              Save vendor & FX
            </Button>
            {hasStored && (
              <Button data-testid="payer-vendor-clear" onClick={clear} variant="outline" className="rounded-xl h-10 px-4">
                Clear
              </Button>
            )}
          </div>
        </div>
      ) : hasStored ? (
        <div className="rounded-xl bg-white/70 border border-purple-200 p-3 text-sm">
          <span className="font-semibold">{payment.payer_vendor_name}</span> · fee {payment.payer_vendor_charge_percent}%
          {payment.settlement_currency && (
            <span className="ml-2 text-slate-600">· settled in {payment.settlement_currency} @ 1 {invoiceCcy} = {payment.settlement_rate}</span>
          )}
        </div>
      ) : null}

      {(canEdit ? selected : hasStored) && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
          <div className="rounded-xl bg-white/70 border border-purple-100 p-3">
            <div className="overline">Invoice amount</div>
            <div className="mt-1 font-mono">{fmtAmount(amt)} {invoiceCcy}</div>
          </div>
          <div className="rounded-xl bg-white/70 border border-purple-100 p-3">
            <div className="overline">Vendor fee ({canEdit ? pct : payment.payer_vendor_charge_percent}%)</div>
            <div className="mt-1 font-mono">
              {fmtAmount(canEdit ? fee : ((payment.payer_final_amount || 0) - amt))} {invoiceCcy}
            </div>
          </div>
          <div className="rounded-xl bg-purple-100 border border-purple-300 p-3">
            <div className="overline text-purple-900">Payer will spend</div>
            <div className="mt-1 font-mono font-bold text-purple-900">
              {fmtAmount(canEdit ? final : payment.payer_final_amount)} {invoiceCcy}
            </div>
          </div>
          {((canEdit && settlementCcy && rateNum > 0) || (!canEdit && payment.settlement_currency && payment.settlement_amount)) && (
            <div className="rounded-xl bg-gradient-to-br from-amber-100 to-amber-50 border border-amber-300 p-3">
              <div className="overline text-amber-900">In {canEdit ? settlementCcy : payment.settlement_currency}</div>
              <div className="mt-1 font-mono font-bold text-amber-900">
                {fmtAmount(canEdit ? settlementAmount : payment.settlement_amount)} {canEdit ? settlementCcy : payment.settlement_currency}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

